import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Spinner } from "reactstrap";
import { Container, Text } from "@mantine/core";
import classNames from "classnames";
import moment from "moment";

import CourseDetails from "components/Course/CourseDetails";

import { useAuth } from "hooks/selectors/useAuth";
import { useCheckout } from "hooks/selectors/useCheckout";
import { fetchCheckoutData } from "reducers/courseRegisterSlice";

import CheckoutSuccessAnimation from "@/views/pages/checkout/components/CheckoutSuccessAnimation";
import DownloadBanner from "@/views/pages/checkout/components/DownloadBanner";
import { CheckoutLogoutButton } from "@/views/pages/checkout/components/LogoutButton";
import { to_official_website_url } from "@/utilities/constant";
import { GTMCheckoutStep3Success, GTMCheckoutStep3SuccessExit } from "utilities/googleTagManager";

import styles from "./CheckoutSuccessPage.module.scss";

export function CheckoutSuccessPage() {
	let navigate = useNavigate();
	const { registrationform_id, courserun_id, course, courserun, hasLoaded, isLoading } =
		useCheckout();

	const dispatch = useDispatch();
	const { checkoutAnalyticsData } = useCheckout();
	const [isFirstTime, setIsFirstTime] = useState(true);
	const { userData } = useAuth();

	// Here will have double api call (one at CheckoutPage and one at CheckoutSuccessPage)
	// But should be fine, since the api call is not heavy and there is lesser user complete the full checkout flow
	useEffect(() => {
		dispatch(
			fetchCheckoutData({
				courserun_id: courserun_id,
				registrationform_id: registrationform_id,
			}),
		);
	}, []);

	useEffect(() => {
		return () => {
			if (history.action === "POP") {
				history.go(1);
			}
		};
	}, [history]);

	useEffect(() => {
		if (isFirstTime) {
			GTMCheckoutStep3Success(userData);
			setIsFirstTime(false);
		}

		// analytics for close tab
		const handleTabClosing = () => {
			GTMCheckoutStep3SuccessExit(userData);
		};

		window.addEventListener("unload", handleTabClosing);
		return () => {
			window.removeEventListener("unload", handleTabClosing);
		};
	}, [userData]);

	return (
		<Container>
			<div className={styles.success_title_container}>
				<CheckoutSuccessAnimation />
				<Text fw="900" fz="xl" my={"xl"} ta="center">
					{"Congratulations! You have successfully signed up for the course below:"}
				</Text>
				<span className={styles.desc_text}>
					{
						"You will receive an email notification shortly. Our Student Support Executive will also be in contact with you prior to the class to assist you with the preparation."
					}
				</span>

				<Alert color="primary" className={classNames(styles.sfc_text, "mt-4")}>
					{isLoading ? (
						<Spinner size={"sm"} />
					) : (
						"⏰ Please allow us up to one working day for your SkillsFuture claim to be confirmed."
					)}
				</Alert>
			</div>
			<div className={styles.success_course_container}>
				<CourseDetails
					logos={course.logos.map((logo) => logo.course_logo_image)}
					courseTitle={course.course_title}
					courseId={course.course_ref}
					location={
						<div>
							<div>{courserun.venue.traininglocation.traininglocation_title}</div>
							<div
								dangerouslySetInnerHTML={{
									__html: courserun.venue.traininglocation.traininglocation_address?.replace(
										/\\r\\n/g,
										"",
									),
								}}
							/>
						</div>
					}
					courseDate={
						moment(courserun.courserun_course_from).format("DD MMMM YYYY, dddd") +
						" - " +
						moment(courserun.courserun_course_to).format("DD MMMM YYYY, dddd")
					}
					courseTimings={courserun.csessions_string}
					learningMode={courserun.courserun_modeoftraining}
					studentSupportName={
						courserun.training_support ? courserun.training_support.user_name : "Updating Soon"
					}
					studentSupportContact={
						courserun.training_support ? courserun.training_support.user_mobile : "Updating Soon"
					}
					trainerImage={courserun.trainer.trainer_image_url}
					trainerName={courserun.trainer.trainer_name}
				/>
				<DownloadBanner />

				<div className={styles.button_container}>
					<Button
						className={`btn-outline-green ${styles.button}`}
						onClick={() => navigate("/login")}>
						<span> Visit to ALCOMM </span>
					</Button>

					<a href={to_official_website_url("/")} className={`btn btn-gradient ${styles.button}`}>
						Back To Home
					</a>
				</div>
			</div>

			<div className="mt-4">
				<CheckoutLogoutButton />
			</div>
		</Container>
	);
}

// process.env.REACT_APP_authDomain,
